import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FileText, Tags, Trash2, X, CheckCircle, Upload, Edit3, AlertTriangle } from "lucide-react";
import { useDocumentCategories } from "../../contexts/DocumentCategoriesContext";

interface DocumentEditModalProps {
    fileName: string;
    visible: boolean;
    onConfirm: (name: string, fileType: string) => void;
    onCancel: () => void;
    isEditMode?: boolean;
    onDelete?: () => void;
}

export const DocumentEditModal: React.FC<DocumentEditModalProps> = ({
                                      fileName,
                                      visible,
                                      onConfirm,
                                      onCancel,
                                      isEditMode = false,
                                      onDelete
                                  }) => {
    const { categories } = useDocumentCategories();
    const fileTypeOptions = useMemo(() => categories.map(c => ({ value: c.key, label: c.label })), [categories]);

    const [name, setName] = useState("");
    const [selectedType, setSelectedType] = useState(fileTypeOptions[0]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        setName(fileName);
    }, [fileName]);

    useEffect(() => {
        if (fileTypeOptions.length > 0 && !selectedType) {
            setSelectedType(fileTypeOptions[0]);
        }
    }, [fileTypeOptions]);

    if (!visible) return null;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (name.trim() && selectedType) {
            onConfirm(name.trim(), selectedType.value);
            setName("");
        }
    };

    const handleCancel = () => {
        onCancel();
        setName("");
        setShowDeleteConfirm(false);
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete();
        }
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    return (
        <>
            <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered modal-sm">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isEditMode ? (
                                    <Edit3 className="me-2" size={18} />
                                ) : (
                                    <FileText className="me-2" size={18} />
                                )}
                                {isEditMode ? "Redigera filen" : "Information om filen"}
                            </h5>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="fileName" className="form-label">
                                        <FileText className="me-1" size={14} />
                                        Namn
                                    </label>
                                    <input
                                        id="fileName"
                                        type="text"
                                        className="form-control"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Ange filnamn"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        <Tags className="me-1" size={14} />
                                        Kategori
                                    </label>
                                    <Select
                                        options={fileTypeOptions}
                                        value={selectedType}
                                        onChange={(val) => {
                                            if (val) setSelectedType(val);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                {isEditMode && onDelete && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger me-auto"
                                        onClick={handleDeleteClick}
                                    >
                                        <Trash2 className="me-1" size={14} />
                                        Radera
                                    </button>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCancel}
                                >
                                    <X className="me-1" size={14} />
                                    Avbryt
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    {isEditMode ? (
                                        <CheckCircle className="me-1" size={14} />
                                    ) : (
                                        <Upload className="me-1" size={14} />
                                    )}
                                    {isEditMode ? "Spara" : "Ladda upp"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title text-danger">
                                    <AlertTriangle className="me-2" size={20} />
                                    Radera fil
                                </h5>
                            </div>
                            <div className="modal-body pt-2">
                                <p className="mb-0">
                                    Är du säker på att du vill ta bort den här filen?
                                </p>
                                <p className="text-muted small mb-0 mt-2">
                                    Denna åtgärd kan inte ångras.
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleDeleteCancel}
                                >
                                    Avbryt
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <Trash2 className="me-1" size={14} />
                                    Radera
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
